import { createContentfulProps } from "frontend/contentful/lib/create-contentful-props";
import { BentoBoxColumnEntry } from "frontend/contentful/schema/primitives";
import { BentoBoxColumnProps } from "design-system/sites/baker/bento-box/bento-column";
import { RenderContentfulRichText } from "../utils/render-contentful-rich-text";
import { useContentful } from "frontend/hooks/use-contentful";
import { useSearchQuery } from "frontend/hooks/use-search-query";
import { useSearch } from "frontend/hooks/use-search";
import { ModernSearchHit, SearchQuery } from "frontend/types";
import { createSearchTease } from "frontend/contentful/search/create-search-tease";
import { isValidElement, ReactNode } from "react";
import { isSearchHitType } from "frontend/contentful/search/is-search-hit-type";
import { getTabHash } from "design-system/components/primitives/tabs/tabs";
import { usePageProps } from "frontend/hooks/use-page-props";
import { getSiteId } from "frontend/lib/get-site-id";
import { LabelLink } from "frontend/lib/es/es-types";
import { DataLayerEvent } from "frontend/hooks/use-data-layer";

interface Options {
  tabTitle?: string;
  headerAbove?: boolean;
}

export const createBentoBoxColumnProps = createContentfulProps<
  BentoBoxColumnEntry,
  BentoBoxColumnProps,
  Options
>(({ entry, createEditAttributes, options }) => {
  const { getEntry } = useContentful();
  const page = usePageProps();
  const site = getEntry(page.site);
  const siteId = getSiteId(site);
  const tabTitle = options?.tabTitle;
  const headerAbove = options?.headerAbove || false;

  const contentSourceEntry = getEntry(entry.fields.contentSource);

  let filters = "";
  if (contentSourceEntry?.fields.query) {
    filters += contentSourceEntry.fields.query;
  }

  const sort = contentSourceEntry?.fields?.sort?.join(",");

  const { searchQuery, debouncedQuery } = useSearchQuery();

  const searchQueryIndex = contentSourceEntry?.fields.index || "modern";
  const searchQueryParams: SearchQuery = {
    index: searchQueryIndex,
    query: debouncedQuery,
    filters,
    from: 0,
    size: 5,
    sort,
  };

  const { data, isLoading } = useSearch(searchQueryParams);

  const bentoItems: BentoBoxColumnProps["bentoItems"] = [];

  data?.hits?.forEach((hit, i) => {
    if (
      isSearchHitType<ModernSearchHit>("modern", hit) &&
      searchQueryIndex === "hollis"
    ) {
      const type =
        "category" in hit &&
        hit.category &&
        typeof hit.category === "object" &&
        "label" in hit.category &&
        typeof hit.category?.label === "string"
          ? hit.category.label
          : undefined;

      const item: NonNullable<BentoBoxColumnProps["bentoItems"]>[number] = {
        title: hit.title,
        titleLink: hit.url,
        description: (type?.includes("Article")
          ? [...(hit.author || []), hit.description]
          : [
              ...(hit.author || []),
              "date" in hit && typeof hit.date === "string"
                ? hit.date
                : undefined,
            ]
        )
          .filter(
            (line): line is string => Boolean(line) && typeof line === "string",
          )
          .map((line) => <p key={line}>{line}</p>),
      };

      if (type) {
        item.category = type;
      }

      bentoItems.push(item);
    } else {
      const teaseItem = createSearchTease(
        hit,
        searchQueryIndex,
        undefined,
        siteId,
      );
      if (teaseItem?.props) {
        if (
          "title" in teaseItem.props &&
          (typeof teaseItem.props.title === "string" ||
            isValidElement(teaseItem.props.title))
        ) {
          const item: NonNullable<BentoBoxColumnProps["bentoItems"]>[number] = {
            title: teaseItem.props.title,
            titleLink:
              "link" in teaseItem.props ? teaseItem.props.link : undefined,
            description:
              "tease" in teaseItem.props
                ? (teaseItem.props.tease as ReactNode)
                : undefined,
          };

          if (
            "overline" in teaseItem.props &&
            teaseItem.props.overline &&
            typeof teaseItem.props.overline !== "string"
          ) {
            const overline: LabelLink = teaseItem.props.overline;
            item.category = overline.label;
          } else if (
            "category" in teaseItem.props &&
            teaseItem.props.category &&
            typeof teaseItem.props.category !== "string" &&
            "label" in teaseItem.props.category &&
            teaseItem.props.category.label
          ) {
            item.category = teaseItem.props.category.label;
            item.categoryLink = teaseItem.props.category.link;
          }

          if (
            teaseItem.name === "ArticleTease" &&
            teaseItem.props?.downloadLink
          ) {
            item.downloadLink = teaseItem.props.downloadLink;
            item.display = teaseItem.props.display;
          }

          if (item.title) {
            /* eslint-disable camelcase */
            const title =
              teaseItem.props &&
              "title" in teaseItem.props &&
              teaseItem.props.title
                ? teaseItem.props?.title
                : undefined;
            const url =
              teaseItem.props &&
              "link" in teaseItem.props &&
              teaseItem.props.link
                ? teaseItem.props?.link
                : undefined;
            const dataLayer: DataLayerEvent = {
              event: "search_result_click",
              event_data: {
                search_term: searchQuery,
                search_result_title: title?.toString(),
                search_result_url: url,
                search_result_position: i + 1,
                search_result_page: 1,
                search_result_tab: tabTitle,
                bento_box_category_clicked: entry.fields.title,
              },
            };
            /* eslint-enable camelcase */

            item.dataLayer = dataLayer;
            bentoItems.push(item);
          }
        }
      }
    }
  });

  const props: BentoBoxColumnProps = {
    title: entry.fields.title || "",
    subtitle: entry.fields.description ? (
      <RenderContentfulRichText
        document={entry.fields.description}
        disableParagraphs
      />
    ) : undefined,
    noResultsText: entry.fields.noResultsText ? (
      <RenderContentfulRichText
        document={entry.fields.noResultsText}
        disableParagraphs
      />
    ) : undefined,
    showEmpty: !debouncedQuery,
    bentoItems,
    results:
      data?.total !== undefined
        ? `${data.total.toLocaleString("en-US")} result${
            data.total === 1 ? "" : "s"
          }`
        : undefined,
    isLoading,
    HeadingLevel: headerAbove ? "h3" : "h2",
    HeadingLevelItem: headerAbove ? "h4" : "h3",

    editAttributes: {
      title: createEditAttributes({ entry, fieldId: "title" }),
      subtitle: createEditAttributes({ entry, fieldId: "description" }),
      noResultsText: createEditAttributes({ entry, fieldId: "noResultsText" }),
      viewAll: createEditAttributes({ entry, fieldId: "ctaLabel" }),
    },
  };

  if (entry.fields.ctaLabel) {
    props.viewAllText = entry.fields.ctaLabel;
  }

  if (searchQueryIndex === "hollis") {
    props.viewAllLink = `https://hollis.harvard.edu/primo-explore/search?query=any,contains,${encodeURIComponent(
      debouncedQuery,
    )}&tab=everything&search_scope=everything&vid=HVD2`;
  } else if (entry.fields.ctaLink) {
    // This only supports linking to tabs on the same page
    // TODO: handle linking to tabs on different pages
    const tabEntry = getEntry(entry.fields.ctaLink);
    const hash = getTabHash(tabEntry?.fields?.title);

    const url = new URL(window.location.href);
    url.hash = `#${hash}`;

    // Reset filters and pagination of tab we are linking to, but not others
    if (hash) {
      Array.from(url.searchParams.keys()).forEach((key) => {
        if (key.startsWith(hash)) {
          url.searchParams.delete(key);
        }
      });
    }

    props.viewAllLink = url.toString().replace(url.origin, "");
  }

  return props;
});
